import { DEFAULT_CURRENCY_CODE, LOCALE_ID, enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { environment } from './environments/environment';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { WindowRef } from './app/services/windowref.service';
import { DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEnGb from '@angular/common/locales/en-GB';
import { buildFormattingLocale, buildLanguage, languageFor } from './app/common/formatting-locale';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter, withRouterConfig } from '@angular/router';
import { ICON_SETTINGS } from '@progress/kendo-angular-icons';
import { ConfigurationService } from './app/services/configuration.service';
import { routeConfig } from './app/app.routes';
import { AppProfileGuard } from './app/auth/app-profile.guard';
import { AuthGuard } from './app/auth/auth.guard';
import { AuthorizeInterceptor } from './app/interceptors/authorize.interceptor';
import { AuthorizeService } from './app/auth/authorize.service';
import { CustomSellerInfoGuard } from './app/auth/custom-seller-info.guard';
import { ShopAccessGuard } from './app/auth/shopaccess.guard';
import { LanguageInterceptor } from './app/interceptors/language.interceptor';
import { MailService } from './app/services/mail.service';
import { UIConfigurationService } from './app/services/uiconfiguration.service';
import { provideAnimations} from '@angular/platform-browser/animations';
import { ApiAuthorizationModule } from './app/auth/api-authorization.module';
import { provideToastr } from 'ngx-toastr';
import { MessageService } from '@progress/kendo-angular-l10n';
import { KendoMessagesService } from './app/services/kendo-messages.service';

// backend #693: both formatting locales are registered, not just the build's own, so the
// LOCALE_ID chosen below always has data behind it. Angular's `de` IS CLDR German (there is no
// separate de-DE file); `en-GB` is the European-English pairing the backend decided.
registerLocaleData(localeDe);
registerLocaleData(localeEnGb);

// A localized build rewrites index.html's `<html lang>` per locale, so this only ever corrects
// the one case that build cannot: `ng serve`, which compiles a single bundle and serves the
// source index.html with its hardcoded `lang="de"`. Cheap, and it keeps `npm run start-en` from
// telling a screen reader that an English page is German.
document.documentElement.lang = languageFor(buildLanguage());

if (environment.production) {
  enableProdMode();
}

/**
 * #178 slice E: Kendo's CLDR data for the locale this bundle renders in.
 *
 * Kendo has its own CLDR, separate from Angular's, and this file imported **`de` only** — in both
 * bundles. Kendo's built-in fallback is `en`, whose territory is **US**, so since #180 pointed
 * LOCALE_ID at `en-GB` the English bundle has been formatting Kendo's dates US-style: a date
 * picker writing `9/7/2026` on the same page an Angular `| date` writes `07/09/2026`. This is the
 * other half of backend #693's rule, and the pairing is the same one.
 *
 * Two explicit branches rather than a computed import path, so the bundler emits exactly these
 * two chunks and nothing wider. The branch is decided at RUNTIME, off `$localize.locale`, so both
 * chunks are written into each locale's output — only one is ever fetched. The data leaves the
 * initial bundle either way, which is the part that was not optional: carrying both locales there
 * to make the choice statically would put ~140 kB of CLDR in front of every first paint.
 */
function loadKendoCldr(): Promise<unknown> {
  return languageFor(buildLanguage()) === 'en'
    ? import('@progress/kendo-angular-intl/locales/en-GB/all')
    : import('@progress/kendo-angular-intl/locales/de/all');
}

// Awaited before bootstrap: a Kendo component that formats a date during its first render would
// otherwise throw `NoLocale` (or silently use the US fallback) for the split second before the
// data lands.
loadKendoCldr().then(() =>
  bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    // onSameUrlNavigation:'reload' lets a "Suchen" click with unchanged search text
    // re-trigger the search: navigating to the identical /search?... URL still emits a
    // navigation (SearchComponent re-runs off NavigationEnd) instead of being ignored.
    provideRouter(routeConfig, withRouterConfig({ onSameUrlNavigation: 'reload' })),
    importProvidersFrom(ApiAuthorizationModule), 
    provideAnimations(),
    provideToastr(),
    WindowRef,
    ConfigurationService,
    MailService,
    UIConfigurationService,
    AuthGuard,
    AppProfileGuard,
    CustomSellerInfoGuard,
    ShopAccessGuard,
    AuthorizeService,
    DatePipe,
    DecimalPipe,
    { provide: HTTP_INTERCEPTORS, useClass: AuthorizeInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    // backend #693: LOCALE_ID is DERIVED from the bundle's own locale, never pinned. A fixed
    // value is what broke this before — pinning 'de' made the English bundle report German, so
    // `@Inject(LOCALE_ID)` could not tell the locales apart and dates rendered German under /en/.
    // The factory keeps that property (each bundle still reports its own language) while fixing
    // the formatting: the English bundle formats as en-GB, so a date reads 07/09/2026 rather than
    // US-style 9/7/2026, matching what the server writes in mails and on the invoice.
    // Consumers that compare locales already fold a region variant to its primary subtag
    // (see LanguageMenuComponent.knownLocale), so en-GB still reads as "en".
    { provide: LOCALE_ID, useFactory: buildFormattingLocale },
    // The currency is EUR in every locale, and never comes from the locale.
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: ICON_SETTINGS, useValue: { type: 'svg' } },
    // #648 / #178 slice E: German texts for the Kendo components this app renders — the grid's
    // empty and sort states, the filter operators, the dropdowns, the date picker, the file
    // picker and the editor toolbar. English comes from Kendo's own defaults.
    { provide: MessageService, useClass: KendoMessagesService },
    provideHttpClient(withInterceptorsFromDi()),
  ]}),
);
